import React, { useEffect, useMemo, useRef, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import {
    collection,
    doc,
    getDocs,
    query,
    setDoc,
    updateDoc,
    where,
} from "firebase/firestore";
import { ref as storageRef, uploadBytes } from "firebase/storage";
import { auth, db, storage } from "../firebase-config";

function toMillis(value) {
    if (!value) return 0;
    if (typeof value === "number") return value;
    if (value.seconds) return value.seconds * 1000;
    if (value.toDate) return value.toDate().getTime();
    return 0;
}

function formatDateTime(value) {
    const ms = toMillis(value);
    if (!ms) return "Unknown";
    return new Date(ms).toLocaleString();
}

function formatDateOnly(value) {
    const ms = toMillis(value);
    if (!ms) return "";
    const date = new Date(ms);
    return date.toISOString().split("T")[0];
}

function AppointmentsPage() {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [notes, setNotes] = useState([]);
    const [pendingRecordings, setPendingRecordings] = useState([]);
    const [patientsById, setPatientsById] = useState({});
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedDate, setSelectedDate] = useState("");
    const [expandedId, setExpandedId] = useState("");
    const [editingId, setEditingId] = useState("");
    const [editText, setEditText] = useState("");
    const [saveStatus, setSaveStatus] = useState("");
    const [saving, setSaving] = useState(false);
    const [addendumRecordingNoteId, setAddendumRecordingNoteId] = useState("");
    const [addendumRecordingId, setAddendumRecordingId] = useState("");
    const [addendumRecording, setAddendumRecording] = useState(false);
    const [addendumReady, setAddendumReady] = useState(false);
    const [addendumStatus, setAddendumStatus] = useState("");
    const [addendumSaving, setAddendumSaving] = useState(false);
    const mediaRecorderRef = useRef(null);
    const addendumRecordingIdRef = useRef("");
    const addendumChunkIndexRef = useRef(-1);

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, (user) => setCurrentUser(user));
        return () => unsub();
    }, []);

    useEffect(() => {
        if (!currentUser) {
            setNotes([]);
            setPatientsById({});
            setLoading(false);
            return;
        }

        const fetchData = async () => {
            setLoading(true);
            setError("");
            try {
                const patientsRef = collection(db, "patients");
                const patientSnap = await getDocs(query(patientsRef, where("ownerId", "==", currentUser.uid)));
                const patientMap = {};
                patientSnap.forEach((docSnap) => {
                    const data = docSnap.data() || {};
                    const name = `${data.firstName || ""} ${data.lastName || ""}`.trim();
                    patientMap[docSnap.id] = {
                        name: name || "Unknown patient",
                    };
                });
                setPatientsById(patientMap);

                const notesList = [];
                const pendingList = [];
                await Promise.all(
                    patientSnap.docs.map(async (patientDoc) => {
                        const notesRef = collection(db, "patients", patientDoc.id, "notes");
                        const notesSnap = await getDocs(notesRef);
                        notesSnap.forEach((docSnap) => {
                            notesList.push({
                                id: docSnap.id,
                                patientId: patientDoc.id,
                                ...docSnap.data(),
                                itemType: "note",
                            });
                        });

                        const recordingsRef = collection(db, "patients", patientDoc.id, "recordings");
                        const recordingsSnap = await getDocs(recordingsRef);
                        recordingsSnap.forEach((docSnap) => {
                            const data = docSnap.data() || {};
                            if (data.status === "pending_transcription") {
                                pendingList.push({
                                    id: docSnap.id,
                                    patientId: patientDoc.id,
                                    ...data,
                                    itemType: "pending",
                                });
                            }
                        });
                    })
                );

                notesList.sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
                pendingList.sort((a, b) => toMillis(b.recordingStartAt) - toMillis(a.recordingStartAt));
                setNotes(notesList);
                setPendingRecordings(pendingList);
            } catch (err) {
                console.error("Failed to load appointments", err);
                setError("Failed to load appointments. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [currentUser]);

    const filteredNotes = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();
        return notes.filter((note) => {
            const patientName = patientsById[note.patientId]?.name || "";
            const matchesSearch = normalizedSearch
                ? patientName.toLowerCase().includes(normalizedSearch)
                : true;
            const matchesDate = selectedDate
                ? formatDateOnly(note.createdAt) === selectedDate
                : true;
            return matchesSearch && matchesDate;
        });
    }, [notes, patientsById, searchTerm, selectedDate]);

    const filteredPending = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();
        return pendingRecordings.filter((recording) => {
            const patientName = patientsById[recording.patientId]?.name || "";
            const matchesSearch = normalizedSearch
                ? patientName.toLowerCase().includes(normalizedSearch)
                : true;
            const matchesDate = selectedDate
                ? formatDateOnly(recording.recordingStartAt) === selectedDate
                : true;
            return matchesSearch && matchesDate;
        });
    }, [pendingRecordings, patientsById, searchTerm, selectedDate]);

    const combinedItems = useMemo(() => {
        const pendingItems = filteredPending.map((item) => ({
            ...item,
            sortTime: toMillis(item.recordingStartAt),
            itemType: "pending",
        }));
        const noteItems = filteredNotes.map((item) => ({
            ...item,
            sortTime: toMillis(item.createdAt),
            itemType: "note",
        }));
        return [...pendingItems, ...noteItems].sort((a, b) => b.sortTime - a.sortTime);
    }, [filteredPending, filteredNotes]);

    const expandedNote = expandedId
        ? filteredNotes.find((note) => note.id === expandedId)
        : null;

    const isDirty = editingId && expandedNote
        ? editText.trim() !== (expandedNote.clinicalNotes || "").trim()
        : false;

    const startEditing = (note) => {
        setEditingId(note.id);
        setEditText(note.clinicalNotes || "");
        setSaveStatus("");
    };

    const saveEdits = async (note) => {
        if (!note?.patientId) return;
        setSaving(true);
        setSaveStatus("");
        try {
            const ref = doc(db, "patients", note.patientId, "notes", note.id);
            await updateDoc(ref, {
                clinicalNotes: editText.trim(),
                updatedAt: new Date().toISOString(),
            });
            setNotes((prev) => prev.map((item) =>
                item.id === note.id ? { ...item, clinicalNotes: editText.trim() } : item
            ));
            setSaveStatus("Clinical note saved.");
            setEditingId("");
        } catch (err) {
            console.error("Failed to save clinical note", err);
            setSaveStatus("Failed to save clinical note.");
        } finally {
            setSaving(false);
        }
    };

    const resetAddendum = () => {
        setAddendumRecording(false);
        setAddendumReady(false);
        setAddendumStatus("");
        setAddendumRecordingId("");
        setAddendumRecordingNoteId("");
        addendumRecordingIdRef.current = "";
        addendumChunkIndexRef.current = -1;
    };

    const persistAddendumChunk = async (note, blob) => {
        if (!auth.currentUser || !note?.patientId) return;
        const userId = auth.currentUser.uid;
        const recordingId =
            addendumRecordingIdRef.current ||
            `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        const nextChunkIndex = (addendumChunkIndexRef.current ?? -1) + 1;
        addendumRecordingIdRef.current = recordingId;
        addendumChunkIndexRef.current = nextChunkIndex;

        const storagePath =
            `recordings/${userId}/${note.patientId}/${recordingId}/chunks/` +
            `${nextChunkIndex}.webm`;
        const fileRef = storageRef(storage, storagePath);

        await uploadBytes(fileRef, blob, {
            contentType: blob.type || "audio/webm",
        });

        await setDoc(
            doc(db, "patients", note.patientId, "recordings", recordingId),
            {
                patientId: note.patientId,
                ownerId: userId,
                status: "pending_transcription",
                type: "addendum",
                noteId: note.id,
                recordingStartAt: Date.now(),
                recordingEndAt: Date.now(),
                updatedAt: new Date().toISOString(),
                createdAt: new Date().toISOString(),
                chunkCount: nextChunkIndex + 1,
                lastChunkIndex: nextChunkIndex,
                lastChunkAt: new Date().toISOString(),
                storagePrefix: `recordings/${userId}/${note.patientId}/${recordingId}/chunks/`,
            },
            { merge: true }
        );

        await setDoc(
            doc(
                db,
                "patients",
                note.patientId,
                "recordings",
                recordingId,
                "chunks",
                `${nextChunkIndex}`
            ),
            {
                index: nextChunkIndex,
                storagePath,
                createdAt: new Date().toISOString(),
                mimeType: blob.type || "audio/webm",
                fileName: `chunk_${nextChunkIndex}.webm`,
            },
            { merge: true }
        );

        setAddendumRecordingId(recordingId);
        setAddendumReady(true);
    };

    const startAddendumRecording = async (note) => {
        if (addendumRecording) return;
        setAddendumStatus("Recording addendum…");
        setAddendumRecordingNoteId(note.id);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
            mediaRecorderRef.current = mediaRecorder;
            const chunks = [];
            mediaRecorder.ondataavailable = (event) => {
                if (event.data?.size > 0) {
                    chunks.push(event.data);
                    persistAddendumChunk(note, event.data).catch((err) => {
                        console.error("Failed to persist addendum chunk", err);
                    });
                }
            };
            mediaRecorder.onstart = () => {
                setAddendumRecording(true);
                setAddendumStatus("Recording addendum…");
            };
            mediaRecorder.onstop = () => {
                setAddendumRecording(false);
                setAddendumStatus("Addendum recorded.");
                try { mediaRecorder.stream.getTracks().forEach((t) => t.stop()); } catch (e) { }
            };
            mediaRecorder.start(60000);
        } catch (err) {
            console.error("Could not start addendum recording", err);
            setAddendumStatus("Microphone access denied.");
        }
    };

    const stopAddendumRecording = () => {
        const mr = mediaRecorderRef.current;
        if (!mr || mr.state === "inactive") return;
        mr.stop();
    };

    const updateClinicalNoteWithAddendum = async (note) => {
        if (!addendumRecordingId) return;
        setAddendumSaving(true);
        setAddendumStatus("");
        try {
            const idToken = await auth.currentUser?.getIdToken();
            if (!idToken) throw new Error("User not authenticated");
            const addendumUrl = process.env.REACT_APP_PROCESS_ADDENDUM_URL;
            if (!addendumUrl) throw new Error("REACT_APP_PROCESS_ADDENDUM_URL is not set");
            const resp = await fetch(addendumUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${idToken}`,
                },
                body: JSON.stringify({
                    recordingId: addendumRecordingId,
                    patientId: note.patientId,
                    noteId: note.id,
                }),
            });
            const data = await resp.json();
            if (!resp.ok) {
                throw new Error(data?.error || "Failed to process addendum");
            }
            const updated = data?.data?.clinicalNotes || "";
            if (updated) {
                setNotes((prev) => prev.map((item) =>
                    item.id === note.id ? { ...item, clinicalNotes: updated } : item
                ));
            }
            setAddendumStatus("Clinical note updated.");
            resetAddendum();
        } catch (err) {
            console.error("Addendum update failed", err);
            setAddendumStatus(err.message || "Failed to update clinical note.");
        } finally {
            setAddendumSaving(false);
        }
    };

    return (
        <div className="page">
            <div className="page-header">
                <div className="page-header-eyebrow">Daily clinical review</div>
                <h1>Appointments</h1>
                <p>Search patients or filter by date to review recordings and notes.</p>
            </div>

            <div className="appointments-controls">
                <input
                    type="text"
                    placeholder="Search by patient name"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                />
                <input
                    type="date"
                    value={selectedDate}
                    onChange={(event) => setSelectedDate(event.target.value)}
                />
                <button
                    className="btn-outline"
                    type="button"
                    onClick={() => {
                        setSearchTerm("");
                        setSelectedDate("");
                    }}
                >
                    Clear
                </button>
            </div>

            {loading && <div className="card">Loading appointments…</div>}
            {error && <div className="card">{error}</div>}

            {!loading && !error && (
                <>
                    {filteredPending.length > 0 && (
                        <div className="appointments-pending">
                            <div className="section-eyebrow">Pending recordings</div>
                            <div className="appointments-list">
                                {filteredPending.map((recording) => {
                                    const patientName = patientsById[recording.patientId]?.name || "Unknown patient";
                                    return (
                                        <div className="appointments-item" key={recording.id}>
                                            <div className="appointments-item-main">
                                                <div className="appointments-patient">{patientName}</div>
                                                <div className="appointments-meta">
                                                    {formatDateTime(recording.recordingStartAt)}
                                                </div>
                                                <div className="appointments-snippet">
                                                    {recording.chunkCount || 0} chunks
                                                </div>
                                            </div>
                                            <div className="appointments-item-side">
                                                <span className="pending-badge">Pending clinical note</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className="appointments-list">
                        {combinedItems.length === 0 && (
                            <div className="card">No appointments match your filters.</div>
                        )}
                        {combinedItems.map((item) => {
                            if (item.itemType === "pending") {
                                const patientName = patientsById[item.patientId]?.name || "Unknown patient";
                                return (
                                    <div className="appointments-item" key={`pending-${item.id}`}>
                                        <div className="appointments-item-main">
                                            <div className="appointments-patient">{patientName}</div>
                                            <div className="appointments-meta">
                                                {formatDateTime(item.recordingStartAt)}
                                            </div>
                                            <div className="appointments-snippet">
                                                {item.chunkCount || 0} chunks
                                            </div>
                                        </div>
                                        <div className="appointments-item-side">
                                            <span className="pending-badge">Pending clinical note</span>
                                        </div>
                                    </div>
                                );
                            }

                            const note = item;
                            const patientName = patientsById[note.patientId]?.name || "Unknown patient";
                            const snippet = (note.clinicalNotes || "").replace(/\s+/g, " ").slice(0, 200);
                            const isExpanded = expandedId === note.id;
                            const isEditing = editingId === note.id;
                            const isAddendumActive = addendumRecordingNoteId === note.id;
                            const canRecordAddendum = !addendumRecording || isAddendumActive;
                            return (
                                <div className="appointments-item" key={note.id}>
                                    <div className="appointments-item-main">
                                        <div className="appointments-patient">{patientName}</div>
                                        <div className="appointments-meta">
                                            {formatDateTime(note.createdAt)}
                                        </div>
                                        <div className="appointments-snippet">
                                            {snippet || "No clinical note content yet."}
                                        </div>
                                        {isExpanded && (
                                            <div className="appointments-expanded">
                                                {!isEditing && (
                                                    <div className="appointments-note-full">
                                                        {note.clinicalNotes || "No clinical note content yet."}
                                                    </div>
                                                )}
                                                {isEditing && (
                                                    <textarea
                                                        className="appointments-editor"
                                                        value={editText}
                                                        onChange={(event) => setEditText(event.target.value)}
                                                        rows={8}
                                                    />
                                                )}
                                                <div className="appointments-actions">
                                                    {!isEditing && (
                                                        <button
                                                            className="btn-outline"
                                                            type="button"
                                                            onClick={() => startEditing(note)}
                                                        >
                                                            Edit
                                                        </button>
                                                    )}
                                                    {isEditing && (
                                                        <button
                                                            className="btn-primary"
                                                            type="button"
                                                            disabled={!isDirty || saving}
                                                            onClick={() => saveEdits(note)}
                                                        >
                                                            {saving ? "Saving…" : "Save"}
                                                        </button>
                                                    )}
                                                    {!isEditing && (
                                                        <button
                                                            className="btn-outline"
                                                            type="button"
                                                            disabled={!canRecordAddendum}
                                                            onClick={() =>
                                                                addendumRecording && isAddendumActive
                                                                    ? stopAddendumRecording()
                                                                    : startAddendumRecording(note)
                                                            }
                                                        >
                                                            {addendumRecording && isAddendumActive
                                                                ? "Stop recording"
                                                                : "🎙 Record addendum"}
                                                        </button>
                                                    )}
                                                    {addendumReady && isAddendumActive && (
                                                        <button
                                                            className="btn-primary"
                                                            type="button"
                                                            disabled={addendumSaving}
                                                            onClick={() => updateClinicalNoteWithAddendum(note)}
                                                        >
                                                            {addendumSaving ? "Updating…" : "Update Clinical Note"}
                                                        </button>
                                                    )}
                                                    {saveStatus && isExpanded && (
                                                        <span className="appointments-save-status">{saveStatus}</span>
                                                    )}
                                                    {addendumStatus && isExpanded && isAddendumActive && (
                                                        <span className="appointments-save-status">{addendumStatus}</span>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="appointments-item-side">
                                        <span className={`note-indicator ${note.clinicalNotes ? "active" : ""}`}>
                                            📝
                                        </span>
                                        <button
                                            className="btn-outline appointments-expand"
                                            type="button"
                                            onClick={() => {
                                                setExpandedId(isExpanded ? "" : note.id);
                                                setEditingId("");
                                                setEditText("");
                                                setSaveStatus("");
                                            }}
                                        >
                                            {isExpanded ? "Collapse" : "Expand"}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}

export default AppointmentsPage;
